import capitalize from 'lodash/capitalize';

import BaseModel from '@core/model';

import VisibilityGroupApi from '@packages/visibility-groups/api';

import Adviser from '@/models/Adviser';
import Investor from '@/models/Investor';
import User from '@/models/User';

export class VisibilityGroup extends BaseModel {
    static name = 'VisibilityGroup';
    static entity = 'visibility_groups';

    /** @type {typeof import('@packages/visibility-groups/api').VisibilityGroupApi} */
    static Api = VisibilityGroupApi;

    static fields() {
        return {
            ...super.fields(),
            id: this.string(null).nullable(),
            name: this.string(null).nullable(),
            description: this.string(null).nullable(),
            enabled: this.boolean(false),
            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id'),
            parent_id: this.string(null).nullable(),
            parent: this.belongsTo(VisibilityGroup, 'parent_id'),
            investors_count: this.number(null).nullable(),
            contacts_count: this.number(null).nullable(),
            grantees_count: this.number(null).nullable(),
            children_count: this.number(null).nullable(),
            total_investors_count: this.number(null).nullable(),
            total_investors_count_loading: this.boolean(false),
            investors: this.modelList(Investor, []),
            contacts: this.modelList(User, []),
            grantees: this.modelList(User, []),
            children: this.modelList(VisibilityGroup, [])
        };
    }

    static getScopedBaseUrl(adviser_id) {
        const version = this.Api.version ? `/${this.Api.version}` : '';
        return `${version}/advisers/${adviser_id}/visibility-groups`;
    }

    static mock(overrides = {}) {
        return {
            id: faker => faker.string.uuid(),
            name: faker => capitalize(faker.word.adjective()) + ' ' + capitalize(faker.animal.type()),
            description: faker => faker.lorem.paragraph(),
            enabled: faker => faker.datatype.boolean(0.5),
            adviser_id: faker => overrides.adviser_id || faker.string.uuid(),
            contacts: faker => {
                const count = faker.number.int({ min: 0, max: 3 });

                return Array.from({ length: count }).map(() => {
                    const name = faker.person.fullName();

                    return {
                        id: faker.string.uuid(),
                        name,
                        email: faker.internet.email({
                            firstName: name.split(' ')[0],
                            lastName: name.split(' ').slice(-1)[0]
                        }),
                        primary_contact: true
                    };
                });
            },
            investors_count: (faker, item) => {
                const contacts = item.contacts || [];

                if (!Array.isArray(contacts) || contacts.length === 0) {
                    return 0;
                }

                return faker.number.int({ min: 1, max: 100 });
            },
            grantees: faker => {
                const count = faker.number.int({ min: 0, max: 5 });

                return Array.from({ length: count }).map(() => {
                    const name = faker.person.fullName();

                    return {
                        id: faker.string.uuid(),
                        name,
                        email: faker.internet.email({
                            firstName: name.split(' ')[0],
                            lastName: name.split(' ').slice(-1)[0]
                        }),
                        primary_contact: faker.datatype.boolean()
                    };
                });
            },
            children: () => []
        };
    }

    static validation() {
        return {
            ENABLEMENT: {
                id: 'required:id',
                name: 'required:name'
            },
            DELETION: {
                id: 'required:id',
                enabled: 'isFalse:enabled'
            }
        };
    }

    get can_be_enabled() {
        const valid = this.$validate('ENABLEMENT');
        return Object.values(valid).every(res => res === true);
    }

    get can_be_deleted() {
        const valid = this.$validate('DELETION');
        return Object.values(valid).every(res => res === true);
    }

    get parents_to_root() {
        if (!this.id) {
            return [];
        }

        const allVisibilityGroups = this.constructor.all();
        const scopedAdviserId = this.adviser_id != null ? `${this.adviser_id}` : null;
        const groupById = new Map();
        const parentByChildId = new Map();

        allVisibilityGroups.forEach(group => {
            if (!group || group.id == null) {
                return;
            }

            if (scopedAdviserId !== null && `${group.adviser_id}` !== scopedAdviserId) {
                return;
            }

            const groupId = `${group.id}`;
            groupById.set(groupId, group);

            const children = Array.isArray(group.children) ? group.children : [];
            children.forEach(child => {
                if (!child || child.id == null) {
                    return;
                }

                const childId = `${child.id}`;
                if (parentByChildId.has(childId)) {
                    return;
                }

                parentByChildId.set(childId, group);
            });
        });

        const ancestry = [];
        const visited = new Set([`${this.id}`]);
        let current = this;

        while (current && current.id != null) {
            const explicitParentId = current.parent_id != null ? `${current.parent_id}` : null;
            const parent = explicitParentId
                ? groupById.get(explicitParentId) || null
                : parentByChildId.get(`${current.id}`) || null;

            if (!parent || parent.id == null || visited.has(`${parent.id}`)) {
                break;
            }

            ancestry.push(parent);
            visited.add(`${parent.id}`);
            current = parent;
        }

        return ancestry.reverse();
    }

    async $get() {
        this.$loading = true;

        return await this.constructor.$get(this.adviser_id, this.id).finally(() => {
            this.$loading = false;
        });
    }

    static async $get(adviser_id, id) {
        await this.api.get(
            id,
            {},
            {
                base_url: this.getScopedBaseUrl(adviser_id)
            }
        );

        return this.findBy({ adviser_id, id });
    }

    async $remove() {
        this.$loading = true;
        return await this.constructor.$remove(this.adviser_id, this.id).finally(() => {
            this.$loading = false;
        });
    }

    static async $remove(adviser_id, id) {
        const target = this.findBy({ adviser_id, id });
        const ancestorIds = Array.isArray(target?.parents_to_root)
            ? target.parents_to_root.map(parent => parent?.id).filter(parentId => parentId != null)
            : [];

        await this.api.remove(id, {
            base_url: this.getScopedBaseUrl(adviser_id)
        });

        const scopedAdviserId = adviser_id != null ? `${adviser_id}` : null;
        const removedId = id != null ? `${id}` : null;

        this.all().forEach(group => {
            if (!group || group.id == null) {
                return;
            }
            if (removedId !== null && `${group.id}` === removedId) {
                return;
            }
            if (scopedAdviserId !== null && `${group.adviser_id}` !== scopedAdviserId) {
                return;
            }

            const children = Array.isArray(group.children) ? group.children : [];
            const filteredChildren = children.filter(child => child && `${child.id}` !== removedId);

            if (filteredChildren.length === children.length) {
                return;
            }

            this.insertOrUpdate({
                data: {
                    ...group,
                    children: filteredChildren,
                    children_count: filteredChildren.length
                }
            });
        });

        const deleted = this.delete(id);

        if (ancestorIds.length > 0) {
            this.api.refreshVisibilityGroupStatisticsByIdsInBackground(adviser_id, ancestorIds);
        }

        return deleted;
    }

    async $create(config = {}) {
        this.$creating = true;

        return await this.constructor.$create(this.$toPayload(), config).finally(() => {
            this.$creating = false;
        });
    }

    static async $create(data = null, config = {}) {
        if (!data || !data.adviser_id) {
            throw new Error('adviser_id is required when creating a visibility group');
        }

        const { adviser_id } = data;

        const response = await this.api.post(data, {
            ...config,
            base_url: this.getScopedBaseUrl(adviser_id)
        });

        const responseData = {
            ...(response?.data || {}),
            adviser_id: response?.data?.adviser_id || adviser_id
        };

        await this.insertOrUpdate({
            data: { ...responseData, $responseFormat: this.ApiResponseFormatEnum.DETAIL }
        });

        return this.findBy({ adviser_id, id: response.data.id });
    }

    async $update(data = null) {
        this.$updating = true;

        const method = data ? 'PATCH' : 'PUT';
        data = data || this.$toPayload();

        this.$saving = Object.keys(data);

        return await this.constructor.$update(this.adviser_id, this.id, method, data).finally(() => {
            this.$updating = false;
            this.$saving = [];
        });
    }

    static async $update(adviser_id, id, method = 'PUT', data = null) {
        if (!adviser_id) {
            throw new ReferenceError('Invalid adviser_id when calling $update on VisibilityGroup');
        }
        if (!id) {
            throw new ReferenceError('Invalid ID when calling $update on VisibilityGroup');
        }

        const payload = { ...(data || {}) };
        delete payload.adviser_id;

        let response = null;

        if (method === 'PUT') {
            response = await this.api.put(
                { ...payload, id },
                {
                    base_url: this.getScopedBaseUrl(adviser_id)
                }
            );
        } else if (method === 'PATCH') {
            response = await this.api.patch(id, payload, {
                base_url: this.getScopedBaseUrl(adviser_id)
            });
        } else {
            throw new Error(`${this.name} method not found when trying to update`);
        }

        await this.insertOrUpdate({
            data: { ...response.data, $responseFormat: this.ApiResponseFormatEnum.DETAIL }
        });

        if (Object.prototype.hasOwnProperty.call(payload, 'enabled')) {
            this.api.refreshAncestorStatisticsInBackground(adviser_id, id);
        }

        return this.findBy({ adviser_id, id }) || this.find(id);
    }

    /**
     * Update the primary contacts for the VisibilityGroup model.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     * @returns {Promise<VisibilityGroup>}
     */
    async $updatePrimaryContacts(items = []) {
        this.$updating = true;

        return await this.constructor.$updatePrimaryContacts(this.adviser_id, this.id, items).finally(() => {
            this.$updating = false;
        });
    }

    /**
     * Update the primary contacts for the VisibilityGroup model.
     * @param {string} adviser_id - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     * @returns {Promise<VisibilityGroup>}
     */
    static async $updatePrimaryContacts(adviser_id, id, items = []) {
        return await this.api.updatePrimaryContacts(adviser_id, id, items);
    }

    /**
     * Update the grants for the VisibilityGroup model.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     * @returns {Promise<VisibilityGroup>}
     */
    async $updateGrants(items = []) {
        this.$updating = true;

        return await this.constructor.$updateGrants(this.adviser_id, this.id, items).finally(() => {
            this.$updating = false;
        });
    }

    /**
     * Update the grants for the VisibilityGroup model.
     * @param {string} adviser_id - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     * @returns {Promise<VisibilityGroup>}
     */
    static async $updateGrants(adviser_id, id, items = []) {
        return await this.api.updateGrants(adviser_id, id, items);
    }

    /**
     * Update the children for the VisibilityGroup model.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     * @returns {Promise<VisibilityGroup>}
     */
    async $updateChildren(items = []) {
        this.$updating = true;

        return await this.constructor.$updateChildren(this.adviser_id, this.id, items).finally(() => {
            this.$updating = false;
        });
    }

    /**
     * Update the children for the VisibilityGroup model.
     * @param {string} adviser_id - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     * @returns {Promise<VisibilityGroup>}
     */
    static async $updateChildren(adviser_id, id, items = []) {
        return await this.api.updateChildren(adviser_id, id, items);
    }
}

export default VisibilityGroup;
